/**
 * Image Loader
 * 
 * Handles loading and caching of game images (monsters, hex tiles)
 */

import { TerrainType, TERRAIN_NAMES } from '../types/terrain';

// Cache for loaded images (exported for HexRenderer access)
export const imageCache = new Map<string, HTMLImageElement>();

/**
 * Load an image and cache it
 */
export function loadImage(src: string): Promise<HTMLImageElement> {
  // Check cache first
  const cached = imageCache.get(src);
  if (cached && cached.complete && cached.naturalWidth > 0) {
    return Promise.resolve(cached);
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => {
      imageCache.set(src, img);
      resolve(img);
    };
    
    img.onerror = () => {
      reject(new Error(`Failed to load image: ${src}`));
    };
    
    img.src = src;
  });
}

/**
 * Get hex tile SVG path for a terrain type
 * Maps terrain types to their corresponding SVG assets
 */
export function getHexTilePath(terrain: TerrainType, q: number, r: number): string {
  // Map terrain types to SVG filenames
  const terrainSvgMap: Record<TerrainType, string> = {
    [TerrainType.Mountain]: 'mountain',
    [TerrainType.Tundra]: 'grassland', // Tundra uses grassland SVG
    [TerrainType.Hills]: 'hills',
    [TerrainType.Forest]: 'forest',
    [TerrainType.Settlement]: 'settlement',
    [TerrainType.River]: 'river',
    [TerrainType.Desert]: 'desert',
    [TerrainType.Ocean]: 'ocean',
  };
  
  const svgName = terrainSvgMap[terrain];
  return `/assets/hexes/${svgName}.svg`;
}

/**
 * Get monster image path
 */
export function getMonsterImagePath(monsterName: string): string {
  // Special case: use fairy emma image
  if (monsterName.toLowerCase() === 'fairy') {
    return '/assets/monsters/fairy-emma.png';
  }
  
  // Special case: Griffon (code) -> griffin (asset file)
  if (monsterName.toLowerCase() === 'griffon') {
    return '/assets/monsters/griffin.png';
  }
  
  const filename = monsterName.toLowerCase().replace(/\s+/g, '-');
  return `/assets/monsters/${filename}.png`;
}

/**
 * Preload images for better performance
 */
export async function preloadImages(paths: string[]): Promise<void> {
  const loadPromises = paths.map(path => 
    loadImage(path).catch(err => {
      console.warn(`Failed to preload image: ${path}`, err);
      return null;
    })
  );
  
  await Promise.all(loadPromises);
}

/**
 * Clear image cache (useful for memory management)
 */
export function clearImageCache(): void {
  imageCache.clear();
}

